import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Mail, Phone, Bell, Webhook, Plus, X } from 'lucide-react'
import type { AlertChannelConfig } from '@/types/fleetvision'

interface ActionsStepProps {
  state: any
  updateState: (updates: any) => void
}

const CHANNEL_TYPES = [
  {
    type: 'email',
    label: 'Email',
    icon: Mail,
    description: 'Send email notifications',
    placeholder: 'email@example.com',
  },
  {
    type: 'sms',
    label: 'SMS',
    icon: Phone,
    description: 'Send SMS text messages',
    placeholder: '+1 555-123-4567',
  },
  {
    type: 'push',
    label: 'Push Notification',
    icon: Bell,
    description: 'Send push notifications to mobile app',
    placeholder: 'all | fleet_managers | drivers',
  },
  {
    type: 'webhook',
    label: 'Webhook',
    icon: Webhook,
    description: 'Send HTTP POST to a webhook URL',
    placeholder: 'https://example.com/webhook',
  },
]

export function ActionsStep({ state, updateState }: ActionsStepProps) {
  const [enabledChannels, setEnabledChannels] = useState<Set<string>>(new Set())
  const [channelTargets, setChannelTargets] = useState<Record<string, string>>({})

  useEffect(() => {
    // Initialize from existing channels
    if (state.channels && state.channels.length > 0) {
      const enabled = new Set<string>(state.channels.map((c: AlertChannelConfig) => c.type))
      const targets: Record<string, string> = {}
      state.channels.forEach((c: AlertChannelConfig) => {
        targets[c.type] = c.target
      })
      setEnabledChannels(enabled)
      setChannelTargets(targets)
    }
  }, [])

  useEffect(() => {
    // Build channels array from enabled channels and targets
    const channels: AlertChannelConfig[] = Array.from(enabledChannels)
      .filter((type) => channelTargets[type]?.trim())
      .map((type) => ({
        type: type as any,
        target: channelTargets[type],
      }))
    updateState({ channels })
  }, [enabledChannels, channelTargets])

  const handleChannelToggle = (type: string, checked: boolean) => {
    const newEnabled = new Set(enabledChannels)
    if (checked) {
      newEnabled.add(type)
    } else {
      newEnabled.delete(type)
    }
    setEnabledChannels(newEnabled)
  }

  const handleTargetChange = (type: string, target: string) => {
    setChannelTargets({
      ...channelTargets,
      [type]: target,
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Actions & Channels</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Configure how you want to be notified when this alert triggers
        </p>
      </div>

      <div className="space-y-4">
        {CHANNEL_TYPES.map((channel) => {
          const Icon = channel.icon
          const isEnabled = enabledChannels.has(channel.type)

          return (
            <Card key={channel.type} className="p-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id={channel.type}
                    checked={isEnabled}
                    onCheckedChange={(checked) =>
                      handleChannelToggle(channel.type, checked as boolean)
                    }
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor={channel.type}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{channel.label}</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {channel.description}
                    </p>
                  </div>
                </div>

                {isEnabled && (
                  <div className="ml-7 space-y-2">
                    <Label className="text-xs">Target</Label>
                    <Input
                      placeholder={channel.placeholder}
                      value={channelTargets[channel.type] || ''}
                      onChange={(e) => handleTargetChange(channel.type, e.target.value)}
                    />
                    {channel.type === 'email' && (
                      <p className="text-xs text-muted-foreground">
                        Separate multiple emails with commas
                      </p>
                    )}
                    {channel.type === 'push' && (
                      <p className="text-xs text-muted-foreground">
                        Use 'all' for all users, or specify a group
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )
        })}
      </div>

      {state.channels.length === 0 && (
        <Card className="p-4 border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Please select at least one notification channel to continue
          </p>
        </Card>
      )}

      {state.channels.length > 0 && (
        <Card className="p-4 bg-muted/50">
          <p className="text-sm">
            <strong>Active channels:</strong>{' '}
            {state.channels.map((c: AlertChannelConfig, i: number) => (
              <span key={i}>
                {i > 0 && ', '}
                {c.type} ({c.target})
              </span>
            ))}
          </p>
        </Card>
      )}
    </div>
  )
}
